import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { authFailure, authSuccess, clearAuthError, startAuth } from '../redux/authSlice';
import { login as loginService, register as registerService } from '../services/authService';

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authState = useAppSelector((state) => state.auth);

  const [showLogin, setShowLogin] = useState(true);
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirm, setRegisterConfirm] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const redirectPath = useMemo(() => {
    const state = location.state as { from?: string } | undefined;
  return state?.from ?? '/app/chat';
  }, [location.state]);

  const authError = authState.error;
  const isLoading = authState.status === 'loading';

  useEffect(() => {
    if (authState.accessToken) {
      navigate(redirectPath, { replace: true });
    }
  }, [authState.accessToken, navigate, redirectPath]);

  const extractErrorMessage = (error: unknown, fallback: string) => {
    if (isAxiosError(error)) {
      const responseData = error.response?.data as unknown;

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }

      if (responseData && typeof responseData === 'object') {
        const detailValue = (responseData as { detail?: unknown }).detail;
        if (typeof detailValue === 'string' && detailValue.trim()) {
          return detailValue;
        }

        if (detailValue && typeof detailValue === 'object') {
          const combined = Object.values(detailValue as Record<string, unknown>)
            .filter((value): value is string => typeof value === 'string')
            .join(' ')
            .trim();
          if (combined) {
            return combined;
          }
        }

        const messageValue = (responseData as { message?: unknown }).message;
        if (typeof messageValue === 'string' && messageValue.trim()) {
          return messageValue;
        }
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  };

  const resetFeedback = () => {
    setPasswordError(null);
    setFormSuccess(null);
    dispatch(clearAuthError());
  };

  const clearLoginFields = () => {
    setLoginIdentifier('');
    setLoginPassword('');
  };

  const clearRegisterFields = () => {
    setRegisterUsername('');
    setRegisterEmail('');
    setRegisterPassword('');
    setRegisterConfirm('');
  };

  const toggleForm = (isLogin: boolean) => {
    setShowLogin(isLogin);
    resetFeedback();

    if (isLogin) {
      clearRegisterFields();
    } else {
      clearLoginFields();
    }
  };

  const togglePassword = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    if (input) {
      input.type = input.type === 'password' ? 'text' : 'password';
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    resetFeedback();

    const identifier = loginIdentifier.trim();
    if (!identifier || !loginPassword) {
      dispatch(authFailure('请输入用户名和密码'));
      return;
    }

    dispatch(startAuth());
    try {
      const payload = identifier.includes('@')
        ? { email: identifier, password: loginPassword }
        : { username: identifier, password: loginPassword };
      const response = await loginService(payload);
      dispatch(authSuccess(response));
      setFormSuccess('登录成功，正在跳转...');
    } catch (error) {
      const message = extractErrorMessage(error, '登录失败，请稍后再试。');
      dispatch(authFailure(message));
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    resetFeedback();

    if (registerPassword !== registerConfirm) {
      const message = '两次密码输入不一致';
      setPasswordError(message);
      dispatch(authFailure(message));
      return;
    }

    const username = registerUsername.trim();
    if (!username || !registerPassword) {
      dispatch(authFailure('请完整填写注册信息'));
      return;
    }

    const payload = {
      username,
      password: registerPassword,
      email: registerEmail.trim() || undefined,
    };

    dispatch(startAuth());
    try {
      const response = await registerService(payload);
      dispatch(authSuccess(response));
      setPasswordError(null);
      setFormSuccess('注册成功，已为您自动登录。');
      clearRegisterFields();
      setShowLogin(true);
    } catch (error) {
      const message = extractErrorMessage(error, '注册失败，请稍后再试。');
      dispatch(authFailure(message));
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat bg-fixed relative"
      style={{ backgroundImage: "url('/页面背景.jpg')" }}
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl">
          <div className="bg-primary p-6 text-center">
            <h1 className="text-[clamp(1.5rem,3vw,2rem)] font-semibold text-white flex items-center justify-center">
              <i className="fa fa-leaf mr-2 text-secondary"></i>
              中医智能助手
            </h1>
            <p className="text-light/80 mt-1 text-sm">传承中医智慧，呵护健康生活</p>
          </div>

          <div className="p-6">
            <div className="flex border-b border-gray-200 mb-6">
              <button
                type="button"
                onClick={() => toggleForm(true)}
                className={`flex-1 py-2 text-center font-medium ${showLogin ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-tertiary'}`}
              >
                登录
              </button>
              <button
                type="button"
                onClick={() => toggleForm(false)}
                className={`flex-1 py-2 text-center font-medium ${!showLogin ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-tertiary'}`}
              >
                注册
              </button>
            </div>

            {(authError || formSuccess) && (
              <div className="mb-6 space-y-3">
                {authError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {authError}
                  </div>
                )}
                {formSuccess && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                    {formSuccess}
                  </div>
                )}
              </div>
            )}

            {showLogin ? (
              <form onSubmit={handleLogin} className="space-y-4 transition-form">
                <div>
                  <label htmlFor="login-identifier" className="block text-sm font-medium text-tertiary mb-1">
                    用户名
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <i className="fa fa-user"></i>
                    </span>
                    <input
                      type="text"
                      id="login-identifier"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg input-focus"
                      placeholder="请输入用户名或邮箱"
                      required
                      value={loginIdentifier}
                      onChange={(e) => setLoginIdentifier(e.target.value)}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="login-password" className="block text-sm font-medium text-tertiary mb-1">
                    密码
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <i className="fa fa-lock"></i>
                    </span>
                    <input
                      type="password"
                      id="login-password"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg input-focus"
                      placeholder="请输入密码"
                      required
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('login-password')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-primary"
                    >
                      <i className="fa fa-eye-slash"></i>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input type="checkbox" id="remember-me" className="h-4 w-4 text-primary border-gray-300 rounded" defaultChecked />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                      记住我
                    </label>
                  </div>
                  <button
                    type="button"
                    className="text-sm text-primary hover:text-secondary transition-colors"
                  >
                    忘记密码?
                  </button>
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-tertiary text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                >
                  <i className="fa fa-sign-in mr-2"></i> {isLoading ? '登录中...' : '登录'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4 transition-form">
                <div>
                  <label htmlFor="register-username" className="block text-sm font-medium text-tertiary mb-1">
                    用户名
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <i className="fa fa-user-plus"></i>
                    </span>
                    <input
                      type="text"
                      id="register-username"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg input-focus"
                      placeholder="请设置用户名"
                      required
                      value={registerUsername}
                      onChange={(e) => setRegisterUsername(e.target.value)}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="register-email" className="block text-sm font-medium text-tertiary mb-1">
                    电子邮箱
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <i className="fa fa-envelope"></i>
                    </span>
                    <input
                      type="email"
                      id="register-email"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg input-focus"
                      placeholder="请输入电子邮箱"
                      required
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="register-password" className="block text-sm font-medium text-tertiary mb-1">
                    设置密码
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <i className="fa fa-lock"></i>
                    </span>
                    <input
                      type="password"
                      id="register-password"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg input-focus"
                      placeholder="请设置密码"
                      required
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('register-password')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-primary"
                    >
                      <i className="fa fa-eye-slash"></i>
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="register-confirm" className="block text-sm font-medium text-tertiary mb-1">
                    确认密码
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <i className="fa fa-lock"></i>
                    </span>
                    <input
                      type="password"
                      id="register-confirm"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg input-focus"
                      placeholder="请再次输入密码"
                      required
                      value={registerConfirm}
                      onChange={(e) => setRegisterConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('register-confirm')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-primary"
                    >
                      <i className="fa fa-eye-slash"></i>
                    </button>
                  </div>
                  {passwordError && (
                    <p className="mt-1 text-sm text-red-500">{passwordError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-tertiary text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                >
                  <i className="fa fa-user-plus mr-2"></i> {isLoading ? '注册中...' : '注册'}
                </button>
              </form>
            )}

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">或使用以下方式登录</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <button className="flex items-center justify-center py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" type="button">
                <i className="fa fa-weixin text-green-600 text-xl"></i>
              </button>
              <button className="flex items-center justify-center py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" type="button">
                <i className="fa fa-qq text-blue-500 text-xl"></i>
              </button>
              <button className="flex items-center justify-center py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" type="button">
                <i className="fa fa-weibo text-red-500 text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          &copy; 2025 中医智能助手 - 保护您的健康隐私
        </p>
      </div>
    </div>
  );
};

export default LoginPage;